import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import MergeIcon from '@mui/icons-material/Merge';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { Accordion, AccordionDetails, AccordionSummary, Box, Button, ButtonGroup, Fab, IconButton, Table, TableBody, TableCell, TableHead, TableRow, Typography } from "@mui/material";
import React, { createRef, useEffect, useState } from 'react';
import CopyToClipboardButton from './CopyToClipboardButton.js';
import EditableFieldWithWarnings from './EditableFieldWithWarnings.js';
import { buildFieldPaths, getFilesWithUnConsolidatedExtractions, getFormatFunction, getJSONPathCompatibleKey, hasConsolidatedExtractions, renderTreatmentDates } from './PathUtils.js';

/**
 * Builds JSONPath expressions for event detail fields in medical chronology
 * @param {string} documentKey - Key of the document
 * @param {number} itemIndex - Index of the chronology item
 * @param {number} eventIndex - Index of the event detail in detailed_narrative
 * @param {string} fieldName - Name of the field (usually 'value')
 * @returns {Object} Object containing textPath, needsReviewPath, and caveatsPath
 */
export function buildEventDetailPaths(documentKey, itemIndex, eventIndex, fieldName = 'value') {
    return {
        textPath: `$.documents['${documentKey}'].analysis.medical_chronology[${itemIndex}].detailed_narrative[${eventIndex}].${fieldName}`,
        needsReviewPath: `$.documents['${documentKey}'].analysis.medical_chronology[${itemIndex}].detailed_narrative[${eventIndex}].needs_review.${fieldName}`,
        caveatsPath: `$.documents['${documentKey}'].analysis.medical_chronology[${itemIndex}].detailed_narrative[${eventIndex}].caveats.${fieldName}`
    }
}


/**
 * Builds JSONPath expressions for consolidated event detail fields in medical chronology
 * @param {number} reportIndex - Index of the medical report in the consolidated array
 * @param {number} itemIndex - Index of the chronology item
 * @param {number} eventIndex - Index of the event detail in detailed_narrative
 * @param {string} fieldName - Name of the field (usually 'value', 'title', 'heading', or 'body')
 * @returns {Object} Object containing textPath, needsReviewPath, and caveatsPath
 */
export function buildConsolidatedEventDetailPaths(reportIndex, itemIndex, eventIndex, fieldName = 'title') {
    // Handle nested content fields specially
    if (fieldName.startsWith('content[')) {
        return {
            textPath: `$.analysis.medical_reports[${reportIndex}].medical_chronology[${itemIndex}].detailed_narrative[${eventIndex}].${fieldName}`,
            needsReviewPath: `$.analysis.medical_reports[${reportIndex}].medical_chronology[${itemIndex}].detailed_narrative[${eventIndex}].${fieldName.replace(/\.(heading|body)$/, '.needs_review.$1')}`,
            caveatsPath: `$.analysis.medical_reports[${reportIndex}].medical_chronology[${itemIndex}].detailed_narrative[${eventIndex}].${fieldName.replace(/\.(heading|body)$/, '.caveats.$1')}`
        }
    }

    // For title and other top-level fields
    return {
        textPath: `$.analysis.medical_reports[${reportIndex}].medical_chronology[${itemIndex}].detailed_narrative[${eventIndex}].${fieldName}`,
        needsReviewPath: `$.analysis.medical_reports[${reportIndex}].medical_chronology[${itemIndex}].detailed_narrative[${eventIndex}].needs_review.${fieldName}`,
        caveatsPath: `$.analysis.medical_reports[${reportIndex}].medical_chronology[${itemIndex}].detailed_narrative[${eventIndex}].caveats.${fieldName}`
    }
}
export default function MedicalChronology({ caseInfo, setCaseInfo, fetchCaseInfoAndUpdateUI, fileNamesToFilePropertiesMap, displayedDocument, setDisplayedDocument, setSnackBarMessage }) {
    const tableRef = createRef();
    const summaryTableRef = createRef();
    // TODO: When hasConsolidated goes from false to true, animate the appearance of the button group make it noticable.
    const hasConsolidated = hasConsolidatedExtractions(caseInfo, 'medical_records', 'medical_reports');
    const [showConsolidatedOnly, setShowConsolidatedOnly] = useState(hasConsolidated);
    const unconsolidatedFiles = getFilesWithUnConsolidatedExtractions(caseInfo, ['medical_records', 'personal_injury_catchall'], ['medical_chronology'], 'medical_reports');
    const hasUnconsolidated = unconsolidatedFiles.length > 0;
    const isMedicalRecordsInProgress = caseInfo && caseInfo['documents'] && Object.values(caseInfo['documents']).some(doc => doc['analysis_queue']?.includes('categorize') || doc['analysis_queue']?.includes('medical_records') || doc['analysis_queue']?.includes('personal_injury_catchall'));
    const isConsolidationInProgress = caseInfo && caseInfo['analysis_queue']?.includes('medical_reports');
    const isProcessingInProgress = caseInfo && (caseInfo['analysis_queue']?.length > 0 ||
        Object.values(caseInfo['documents'] || {}).some(doc => doc['analysis_queue']?.length > 0));
    assert('caseId' in caseInfo, 'caseId not found in caseInfo');

    // Auto-switch to consolidated view when consolidation completes
    useEffect(() => {
        if (hasConsolidated) {
            setShowConsolidatedOnly(true);
        }
    }, [hasConsolidated]);

    return (
        <Box sx={{ p: 3 }}>
            {/* Header section with ButtonGroup on left and CopyToClipboardButton on right */}
            <Box sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 2
            }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                    {hasConsolidated && (
                        <ButtonGroup
                            sx={{
                                visibility: hasConsolidated ? "visible" : "hidden",
                                pointerEvents: "auto"
                            }}
                            aria-label="view mode button group"
                            variant="text"
                            size="small"
                            className="selectConsolidatedButtonGroup"
                        >
                            <Button
                                onClick={() => setShowConsolidatedOnly(false)}
                                disabled={!showConsolidatedOnly}
                                key="all"
                                size="small"
                            >
                                All Records
                            </Button>
                            <Button
                                onClick={() => setShowConsolidatedOnly(true)}
                                disabled={showConsolidatedOnly}
                                key="consolidated"
                                size="small"
                            >
                                Consolidated Records
                            </Button>
                        </ButtonGroup>
                    )}
                    {isMedicalRecordsInProgress && (
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <img
                                src="/loading.gif"
                                alt="loading..."
                                title="Processing..."
                                style={{
                                    marginRight: "8px",
                                    height: "16px",
                                    verticalAlign: "middle"
                                }}
                            />
                            <Typography
                                variant="body2"
                                sx={{
                                    color: 'var(--text-primary)',
                                    fontStyle: 'italic',
                                    fontSize: '1.1rem',
                                    fontWeight: 500
                                }}
                            >
                                Medical Chronology is being extracted...
                            </Typography>
                        </Box>
                    )}
                    {!isMedicalRecordsInProgress && isConsolidationInProgress && (
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <img
                                src="/loading.gif"
                                alt="loading..."
                                title="Processing..."
                                style={{
                                    marginRight: "8px",
                                    height: "16px",
                                    verticalAlign: "middle",
                                    opacity: 0.7
                                }}
                            />
                            <Typography
                                variant="body2"
                                sx={{
                                    color: 'var(--text-primary)',
                                    fontStyle: 'italic',
                                    fontSize: '1.1rem',
                                    fontWeight: 500
                                }}
                            >
                                Consolidating medical records...
                            </Typography>
                        </Box>
                    )}
                    {((hasConsolidated && hasUnconsolidated) || (unconsolidatedFiles.length > 1)) && !isProcessingInProgress && (
                        <Box sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1.5,
                            px: 2,
                            py: 1,
                            backgroundColor: 'transparent',
                            borderRadius: '8px',
                            border: '1px solid var(--border-color)'
                        }}>
                            <Typography
                                variant="body2"
                                sx={{
                                    color: 'var(--info-color)',
                                }}
                            >
                                {hasConsolidated && hasUnconsolidated
                                    ? `Click Consolidate to incorporate new data extracted from ${unconsolidatedFiles.length} files`
                                    : 'Raw information extracted from files below. Click Consolidate to generate a single, master medical report.'
                                }
                            </Typography>
                            <Fab
                                onClick={async () => {
                                    const response = await fetch('/api/case/' + caseInfo['caseId'] + '/analysis_queue', {
                                        method: 'POST',
                                        credentials: 'same-origin',
                                        headers: new Headers({ 'content-type': 'application/json' }),
                                        body: JSON.stringify({
                                            analysis_queue: ['medical_reports'],
                                            item_type: 'consolidation'
                                        })
                                    });
                                    console.log('Consolidate button clicked: ' + response);

                                    if (response.ok) {
                                        setCaseInfo({ ...caseInfo, analysis_queue: [...caseInfo['analysis_queue'] || [], 'medical_reports'] });
                                        setTimeout(fetchCaseInfoAndUpdateUI, 10000);
                                    }
                                }}
                                variant="extended"
                                size="medium"
                                color="primary"
                                sx={{
                                    top: "-1px",
                                    background: 'var(--button-gradient)',
                                    boxShadow: '0 4px 15px rgba(212, 175, 55, 0.4)',
                                    fontWeight: 600,
                                    textTransform: 'none',
                                    fontSize: '0.95rem',
                                    padding: '14px 24px',
                                    borderRadius: '12px',
                                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                    position: 'relative',
                                    overflow: 'hidden',
                                    border: 'none',
                                    '&:hover': {
                                        transform: 'translateY(-3px)',
                                        boxShadow: '0 8px 25px rgba(212, 175, 55, 0.5)',
                                        background: 'var(--button-gradient-hover)'
                                    },
                                    '&:active': {
                                        transform: 'translateY(-1px)',
                                        boxShadow: '0 4px 15px rgba(212, 175, 55, 0.4)'
                                    },
                                    '&::before': {
                                        content: '""',
                                        position: 'absolute',
                                        top: 0,
                                        left: '-100%',
                                        width: '100%',
                                        height: '100%',
                                        // background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.3), transparent)',
                                        transition: 'left 0.6s'
                                    },
                                    '&:hover::before': {
                                        left: '100%'
                                    }
                                }}
                            >
                                <MergeIcon sx={{ mr: 1 }} style={{ transform: 'rotate(90deg)' }} />
                                Consolidate
                            </Fab>
                        </Box>
                    )}
                </Box>
                {!showConsolidatedOnly && <CopyToClipboardButton nodeRef={tableRef} setSnackBarMessage={setSnackBarMessage} buttonText="Copy Medical Chronology" />}
            </Box>

            {/* Content section below the header */}
            {showConsolidatedOnly ? consolidatedMedicalReports() : medicalChronology()}
        </Box >
    );

    function consolidatedMedicalReports() {
        const medicalReports = caseInfo?.['analysis']?.['medical_reports'] || [];

        if (medicalReports.length === 0) {
            return (
                <Box sx={{
                    p: 3,
                    textAlign: 'center',
                    mt: 8,
                    mb: 8
                }}>
                    <Typography
                        variant="h6"
                        sx={{
                            color: 'var(--text-secondary)',
                            fontWeight: 400,
                            fontSize: '1.1rem',
                            lineHeight: 1.5
                        }}
                    >
                        Consolidated medical reports will appear here after consolidation is complete.
                    </Typography>
                </Box>
            );
        }

        return (
            <Box sx={{ mt: 2 }}>
                {medicalReports.map((report, reportIndex) => (
                    <Accordion
                        key={report.uuid || reportIndex}
                        defaultExpanded={reportIndex === 0}
                        sx={{
                            mb: 2,
                            border: '1px solid var(--border-color)',
                            boxShadow: 'var(--shadow-light)',
                            '&:before': { display: 'none' },
                            backgroundColor: 'var(--background-color)'
                        }}
                    >
                        <AccordionSummary
                            // Make only icon clickable to expand/contract Accordian from https://stackoverflow.com/questions/69358781/material-ui-expand-accordion-by-clicking-the-icon-only
                            sx={{
                                pointerEvents: "none",
                                backgroundColor: 'var(--surface-color)',
                                borderBottom: '1px solid var(--border-color)',
                                height: '64px', // Without this, height of accordian summary changes when expanded or collapsed due to minHeigh rule only applied on expanded by library.
                                padding: '8px 16px',
                                '&:hover': {
                                    backgroundColor: 'var(--surface-color)'
                                }
                            }}
                            expandIcon={<ExpandMoreIcon
                                sx={{
                                    pointerEvents: "auto",
                                    color: 'var(--text-secondary)',
                                    transition: 'all 0.2s ease',
                                    '&:hover': {
                                        color: 'var(--accent-color)',
                                        transform: 'scale(1.1)',
                                    }
                                }}
                            // onClick={onAccordianIconClicked}
                            />}
                            style={{
                                flexDirection: "row-reverse",
                                cursor: "default",
                            }}
                        >
                            <Typography variant="h6" sx={{ fontWeight: 600, color: 'var(--text-primary)' }}>
                                Medical Report for {report.patient_name || 'Unknown Patient'}
                            </Typography>
                        </AccordionSummary>
                        <AccordionDetails sx={{ p: 3 }}>
                            {/* Summary Table */}
                            <Box sx={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                                mb: 2
                            }}>
                                <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, color: 'var(--text-primary)' }}>
                                    Summary
                                </Typography>
                                <CopyToClipboardButton nodeRef={summaryTableRef} setSnackBarMessage={setSnackBarMessage} buttonText="Copy Chronology Summary" />
                            </Box>
                            <Table
                                ref={summaryTableRef}
                                sx={{
                                    mb: 4,
                                    backgroundColor: 'var(--background-color)',
                                    borderRadius: '8px',
                                    border: '1px solid var(--border-color)',
                                    boxShadow: 'var(--shadow-light)'
                                }}
                            >
                                <TableHead>
                                    <TableRow sx={{ backgroundColor: 'var(--surface-color)' }}>
                                        <TableCell sx={{
                                            fontWeight: 600,
                                            color: 'var(--text-primary)',
                                            borderBottom: '2px solid var(--border-color)',
                                            minWidth: "200px"
                                        }}>
                                            Description
                                        </TableCell>
                                        <TableCell sx={{
                                            fontWeight: 600,
                                            color: 'var(--text-primary)',
                                            borderBottom: '2px solid var(--border-color)',
                                            minWidth: "300px"
                                        }}>
                                            Details
                                        </TableCell>
                                        <TableCell sx={{
                                            fontWeight: 600,
                                            color: 'var(--text-primary)',
                                            borderBottom: '2px solid var(--border-color)',
                                            minWidth: "200px"
                                        }}>
                                            Source Document
                                        </TableCell>
                                    </TableRow>
                                </TableHead>

                                <TableBody>
                                    {renderSummaryTableRows(report, reportIndex)}
                                </TableBody>
                            </Table>

                            {/* Medical Chronology Table */}
                            <Box sx={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                                mb: 2
                            }}>
                                < Typography variant="h6" sx={{ mb: 2, fontWeight: 600, color: 'var(--text-primary)' }}>
                                    Medical Chronology
                                </Typography>
                                <CopyToClipboardButton nodeRef={tableRef} setSnackBarMessage={setSnackBarMessage} buttonText="Copy Medical Chronology" />
                            </Box>
                            {renderConsolidatedChronologyTable(report.medical_chronology, reportIndex)}
                        </AccordionDetails >
                    </Accordion >
                ))
                }
            </Box >
        );
    }

    function renderSummaryTableRows(report, reportIndex) {
        const rows = [];

        // Row 1: Pre-existing conditions
        const pastMedicalHistory = report.past_medical_history || [];
        if (pastMedicalHistory.length > 0) {
            pastMedicalHistory.forEach((item, index) => {
                rows.push({
                    description: index === 0 ? 'Pre-existing Conditions' : '',
                    details: item.value,
                    sources: item.sources || [],
                    fieldPaths: buildConsolidatedFieldPaths(reportIndex, 'past_medical_history', index, 'value'),
                    isFirstInGroup: index === 0,
                    groupCount: pastMedicalHistory.length
                });
            });
        } else {
            rows.push({
                description: 'Pre-existing Conditions',
                details: 'None reported',
                sources: [],
                fieldPaths: buildConsolidatedFieldPaths(reportIndex, 'past_medical_history', 0, 'value'),
                isFirstInGroup: true,
                groupCount: 1
            });
        }

        // Row 2: Date of injury
        const incidentSummary = report.incident_summary || {};
        const incidentDate = incidentSummary.incident_date || 'N/A';
        const incidentDateSources = incidentSummary.sources || [];

        rows.push({
            description: 'Date of Injury',
            details: incidentDate,
            sources: incidentDateSources,
            fieldPaths: buildConsolidatedSingleFieldPaths(reportIndex, 'incident_summary', 'incident_date'),
            isFirstInGroup: true,
            groupCount: 1
        });

        // Row 3: Description of injury
        const incidentDescription = incidentSummary.incident_description || 'N/A';
        const incidentDescriptionSources = incidentSummary.sources || [];

        rows.push({
            description: 'Description of Injury',
            details: incidentDescription,
            sources: incidentDescriptionSources,
            fieldPaths: buildConsolidatedSingleFieldPaths(reportIndex, 'incident_summary', 'incident_description'),
            isFirstInGroup: true,
            groupCount: 1
        });

        // Row 4: Injuries as a result of incident
        const injuriesSummary = report.injuries_summary || [];
        if (injuriesSummary.length > 0) {
            injuriesSummary.forEach((injury, index) => {
                rows.push({
                    description: index === 0 ? 'Injuries as a Result of Incident' : '',
                    details: injury.injury_description,
                    sources: injury.sources || [],
                    fieldPaths: buildConsolidatedFieldPaths(reportIndex, 'injuries_summary', index, 'injury_description'),
                    isFirstInGroup: index === 0,
                    groupCount: injuriesSummary.length
                });
            });
        } else {
            rows.push({
                description: 'Injuries as a Result of Incident',
                details: 'None reported',
                sources: [],
                fieldPaths: buildConsolidatedFieldPaths(reportIndex, 'injuries_summary', 0, 'injury_description'),
                isFirstInGroup: true,
                groupCount: 1
            });
        }

        // Row 5: Treatments rendered
        const treatmentsSummary = report.treatments_summary || [];
        if (treatmentsSummary.length > 0) {
            treatmentsSummary.forEach((treatment, index) => {
                rows.push({
                    description: index === 0 ? 'Treatments Rendered' : '',
                    details: treatment.treatment_description,
                    sources: treatment.sources || [],
                    fieldPaths: buildConsolidatedFieldPaths(reportIndex, 'treatments_summary', index, 'treatment_description'),
                    isFirstInGroup: index === 0,
                    groupCount: treatmentsSummary.length
                });
            });
        } else {
            rows.push({
                description: 'Treatments Rendered',
                details: 'None reported',
                sources: [],
                fieldPaths: buildConsolidatedFieldPaths(reportIndex, 'treatments_summary', 0, 'treatment_description'),
                isFirstInGroup: true,
                groupCount: 1
            });
        }

        return rows.map((row, index) => {
            return (
                <TableRow
                    key={index}
                    sx={{
                        '&:hover .source-view-icon': {
                            visibility: 'visible',
                            opacity: 0.7
                        }
                    }}
                >
                    {row.isFirstInGroup && (
                        <TableCell
                            rowSpan={row.groupCount}
                            sx={{
                                fontWeight: 600,
                                color: 'var(--text-primary)',
                                borderBottom: '1px solid var(--border-color)',
                                verticalAlign: 'top',
                                paddingTop: '16px'
                            }}
                        >
                            {row.description}
                        </TableCell>
                    )}
                    <TableCell sx={{
                        borderBottom: '1px solid var(--border-color)'
                    }}>
                        <EditableFieldWithWarnings
                            caseInfo={caseInfo}
                            setCaseInfo={setCaseInfo}
                            {...row.fieldPaths}
                            setSnackBarMessage={setSnackBarMessage}
                            onWarningClick={() => {
                                if (row.sources.length > 0) {
                                    const firstSource = row.sources[0];
                                    setDisplayedDocument({
                                        'key': getJSONPathCompatibleKey(firstSource?.filename || ''),
                                        'page': firstSource?.page || 1
                                    });
                                }
                            }}
                        />
                    </TableCell>
                    <TableCell sx={{
                        borderBottom: '1px solid var(--border-color)'
                    }}>
                        {row.sources.length > 0 ? (
                            <>
                                {row.sources.map((source, sourceIndex) => (
                                    <Box key={sourceIndex} component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                                        <Typography
                                            component="span"
                                            title="Click to view source document"
                                            sx={{
                                                cursor: 'pointer',
                                                textDecoration: 'underline',
                                                fontSize: '0.9rem',
                                            }}
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                setDisplayedDocument({
                                                    'key': getJSONPathCompatibleKey(source?.filename),
                                                    'page': source?.page
                                                });
                                            }}
                                        >
                                            {source?.filename} {source?.page && `(page ${source?.page})`}
                                        </Typography>
                                        <IconButton
                                            className="source-view-icon"
                                            size="small"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                setDisplayedDocument({
                                                    'key': getJSONPathCompatibleKey(source?.filename),
                                                    'page': source?.page
                                                });
                                            }}
                                            sx={{
                                                padding: '2px',
                                                visibility: 'hidden',
                                                opacity: 0,
                                                transition: 'opacity 0.2s, visibility 0.2s',
                                                '&:hover': {
                                                    opacity: 1,
                                                    backgroundColor: 'rgba(0, 0, 0, 0.04)'
                                                }
                                            }}
                                            title="Click to view source document"
                                        >
                                            <VisibilityIcon />
                                        </IconButton>
                                    </Box>
                                )).reduce((prev, curr, index) => [
                                    ...prev,
                                    index > 0 && (
                                        <Typography
                                            key={`comma-${index}`}
                                            component="span"
                                            sx={{
                                                mx: 0.5,
                                                fontSize: '0.75rem'
                                            }}
                                        >
                                            <br />
                                        </Typography>
                                    ),
                                    curr
                                ], [])}
                            </>
                        ) : (
                            'N/A'
                        )}
                    </TableCell>
                </TableRow>
            );
        });
    }

    function renderSourcesList(sources) {
        if (!sources || sources.length === 0) {
            return 'N/A';
        }
        return (
            <Box>
                {sources.map((source, index) => (
                    <Box key={index} sx={{ mb: 0.5, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Typography
                            component="span"
                            title="Click to view source document"
                            sx={{
                                cursor: 'pointer',
                                textDecoration: 'underline',
                                fontSize: '0.9rem',
                            }}
                            onClick={(e) => {
                                e.stopPropagation();
                                setDisplayedDocument({
                                    'key': getJSONPathCompatibleKey(source?.filename),
                                    'page': source?.page
                                });
                            }}
                        >
                            {source?.filename} {source?.page && `(page ${source?.page})`}
                        </Typography>
                        <IconButton
                            className="source-view-icon"
                            size="small"
                            onClick={(e) => {
                                e.stopPropagation();
                                setDisplayedDocument({
                                    'key': getJSONPathCompatibleKey(source?.filename),
                                    'page': source?.page
                                });
                            }}
                            sx={{
                                padding: '2px',
                                visibility: 'hidden',
                                opacity: 0,
                                transition: 'opacity 0.2s, visibility 0.2s',
                                '&:hover': {
                                    opacity: 1,
                                    backgroundColor: 'rgba(0, 0, 0, 0.04)'
                                }
                            }}
                            title="Click to view source document"
                        >
                            <VisibilityIcon />
                        </IconButton>
                    </Box>
                ))}
            </Box>
        );
    }

    function renderConsolidatedChronologyTable(chronologyItems, reportIndex) {
        if (!chronologyItems || chronologyItems.length === 0) {
            return (
                <Typography sx={{ color: 'var(--text-secondary)', fontStyle: 'italic' }}>
                    No chronology items found. Upload documents such as medical records, EOBs, itemized bills, receipts, ledgers, progress notes, etc to extract medical chronology.
                </Typography>
            );
        }

        // Sort chronology items by date
        const sortedItems = [...chronologyItems].sort((item1, item2) =>
            Date.parse(item1.treatment_start_date || item1.treatment_date || '') -
            Date.parse(item2.treatment_start_date || item2.treatment_date || ''));

        return (
            <Table
                ref={tableRef}
                sx={{
                    backgroundColor: 'var(--background-color)',
                    borderRadius: '8px',
                    border: '1px solid var(--border-color)',
                    boxShadow: 'var(--shadow-light)'
                }}
            >
                <TableHead>
                    <TableRow sx={{ backgroundColor: 'var(--surface-color)' }}>
                        <TableCell sx={{
                            fontWeight: 600,
                            color: 'var(--text-primary)',
                            borderBottom: '2px solid var(--border-color)',
                            minWidth: "120px"
                        }}>
                            Treatment Dates
                        </TableCell>
                        <TableCell sx={{
                            fontWeight: 600,
                            color: 'var(--text-primary)',
                            borderBottom: '2px solid var(--border-color)',
                            minWidth: "200px"
                        }}>
                            Provider
                        </TableCell>
                        <TableCell sx={{
                            fontWeight: 600,
                            color: 'var(--text-primary)',
                            borderBottom: '2px solid var(--border-color)',
                            minWidth: "300px"
                        }}>
                            Summary
                        </TableCell>
                        <TableCell sx={{
                            fontWeight: 600,
                            color: 'var(--text-primary)',
                            borderBottom: '2px solid var(--border-color)',
                            minWidth: "200px"
                        }}>
                            Event Details
                        </TableCell>
                        <TableCell sx={{
                            fontWeight: 600,
                            color: 'var(--text-primary)',
                            borderBottom: '2px solid var(--border-color)',
                            minWidth: "200px"
                        }}>
                            Source Document
                        </TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {sortedItems.map((chronologyItem, itemIndex) => {
                        return (
                            <TableRow
                                key={chronologyItem.uuid || itemIndex}
                                sx={{
                                    '&:hover .source-view-icon': {
                                        visibility: 'visible',
                                        opacity: 0.7
                                    }
                                }}
                            >
                                <TableCell sx={{
                                    borderBottom: '1px solid var(--border-color)'
                                }}>
                                    {renderConsolidatedTreatmentDates(chronologyItem, reportIndex, itemIndex)}
                                </TableCell>
                                <TableCell sx={{
                                    color: 'var(--text-primary)',
                                    borderBottom: '1px solid var(--border-color)'
                                }}>
                                    {renderProviders(chronologyItem, '', itemIndex, true, reportIndex)}
                                </TableCell>
                                <TableCell sx={{
                                    color: 'var(--text-primary)',
                                    borderBottom: '1px solid var(--border-color)'
                                }}>
                                    <EditableFieldWithWarnings
                                        caseInfo={caseInfo}
                                        setCaseInfo={setCaseInfo}
                                        {...buildConsolidatedFieldPaths(reportIndex, 'medical_chronology', itemIndex, 'summary')}
                                        formatValue={getFormatFunction('summary')}
                                        setSnackBarMessage={setSnackBarMessage}
                                        onWarningClick={() => {
                                            const sources = chronologyItem.sources || [];
                                            if (sources.length > 0) {
                                                setDisplayedDocument({
                                                    'key': getJSONPathCompatibleKey(sources[0]?.filename || ''),
                                                    'page': sources[0]?.page || 1
                                                });
                                            }
                                        }}
                                    />
                                </TableCell>
                                <TableCell sx={{
                                    color: 'var(--text-primary)',
                                    borderBottom: '1px solid var(--border-color)'
                                }}>
                                    {renderConsolidatedEventDetails(chronologyItem, reportIndex, itemIndex)}
                                </TableCell>
                                <TableCell sx={{
                                    borderBottom: '1px solid var(--border-color)'
                                }}>
                                    {renderSourcesList(chronologyItem.sources || [])}
                                </TableCell>
                            </TableRow >
                        );
                    })}
                </TableBody>
            </Table >
        );
    }

    function renderConsolidatedEventDetails(chronologyItem, reportIndex, itemIndex) {
        if (!chronologyItem.detailed_narrative || chronologyItem.detailed_narrative.length === 0) {
            return '';
        }

        const sources = chronologyItem.sources || [];
        const handleWarningClick = () => {
            if (sources.length > 0) {
                setDisplayedDocument({
                    'key': getJSONPathCompatibleKey(sources[0]?.filename || ''),
                    'page': sources[0]?.page || 1
                });
            }
        };

        return (
            <Box>
                {chronologyItem.detailed_narrative.map((narrativeItem, narrativeIndex) => (
                    <Box key={narrativeIndex} sx={{ mb: 1.5 }}>
                        <EditableFieldWithWarnings
                            caseInfo={caseInfo}
                            setCaseInfo={setCaseInfo}
                            {...buildConsolidatedEventDetailPaths(reportIndex, itemIndex, narrativeIndex, 'title')}
                            setSnackBarMessage={setSnackBarMessage}
                            onWarningClick={handleWarningClick}
                            style={{
                                fontWeight: 600,
                                fontSize: '0.875rem',
                                marginBottom: '4px',
                                display: 'block'
                            }}
                        />
                        {narrativeItem.content && narrativeItem.content.map((contentItem, contentIndex) => (
                            <Box key={contentIndex} sx={{ ml: 2, mb: 0.5 }}>
                                <Box component="span" sx={{ display: 'inline' }}>
                                    <EditableFieldWithWarnings
                                        caseInfo={caseInfo}
                                        setCaseInfo={setCaseInfo}
                                        {...buildConsolidatedEventDetailPaths(reportIndex, itemIndex, narrativeIndex, `content[${contentIndex}].heading`)}
                                        setSnackBarMessage={setSnackBarMessage}
                                        onWarningClick={handleWarningClick}
                                        style={{
                                            fontWeight: 500,
                                            fontSize: '0.875rem',
                                            display: 'inline'
                                        }}
                                    />
                                    <EditableFieldWithWarnings
                                        caseInfo={caseInfo}
                                        setCaseInfo={setCaseInfo}
                                        {...buildConsolidatedEventDetailPaths(reportIndex, itemIndex, narrativeIndex, `content[${contentIndex}].body`)}
                                        setSnackBarMessage={setSnackBarMessage}
                                        onWarningClick={handleWarningClick}
                                        style={{
                                            fontSize: '0.875rem',
                                            display: 'inline'
                                        }}
                                    />
                                </Box>
                            </Box>
                        ))}
                    </Box>
                ))}
            </Box>
        );
    }

    function medicalChronology() {
        // Get document-based medical chronology (All Records)
        var documentKeysToDisplay = caseInfo !== null && 'documents' in caseInfo ?
            Object.keys(caseInfo['documents']).filter(docKey => fileNamesToFilePropertiesMap[caseInfo['documents'][docKey]['filename']]['selected']) :
            [];
        var medicalChronology = documentKeysToDisplay.map(documentKey => {
            caseInfo['documents'][documentKey]['analysis']?.['medical_chronology']?.forEach((chronologyItem, i) => {
                // Add null check to prevent errors
                if (chronologyItem) {
                    chronologyItem['id'] = documentKey + i; // Used in UI as keys for use by React framework
                    chronologyItem['source_filekey'] = documentKey; // File used by SourceDocumentViewer
                }
            });
            return caseInfo['documents'][documentKey]['analysis']?.['medical_chronology'];
        })
            .filter(chronologyArr => chronologyArr) // the ?. above can cause null entries; filter them
            .reduce((accumulator, chronologyArr) => accumulator.concat(chronologyArr), [])
            .filter(chronologyItem => isValidMedicalChronologyItem(chronologyItem)) // Remove '', 'N/A', null, etc
            .sort((item1, item2) => Date.parse(item1.treatment_start_date) - Date.parse(item2.treatment_start_date));

        if (medicalChronology.length === 0) {
            return (
                <Box sx={{
                    p: 3,
                    textAlign: 'center',
                    mt: 8,
                    mb: 8
                }}>
                    <Typography
                        variant="h6"
                        sx={{
                            color: 'var(--text-secondary)',
                            fontWeight: 400,
                            fontSize: '1.1rem',
                            lineHeight: 1.5
                        }}
                    >
                        Medical chronology will be automatically extracted from uploaded medical records and listed here.
                    </Typography>
                </Box>
            )
        }

        var tableBodyRows = medicalChronology
            .map(chronologyItem => {
                // Find the document key for this chronology item
                const documentKey = chronologyItem.source_filekey;
                const docInfo = caseInfo.documents[documentKey];
                const filename = docInfo?.['filename'];
                const itemIndex = caseInfo.documents[documentKey]?.analysis?.medical_chronology?.findIndex(item => item.id === chronologyItem.id) || 0;

                return (
                    <TableRow
                        key={chronologyItem['id']}
                        className={chronologyItem['source_filekey'] === displayedDocument?.['key'] ? "selected" : ""}
                        sx={{
                            '&:hover .source-view-icon': {
                                visibility: 'visible',
                                opacity: 0.7
                            }
                        }}
                    >
                        <TableCell sx={{
                            color: 'var(--text-primary)',
                            borderBottom: '1px solid var(--border-color)',
                            fontWeight: 500
                        }}>
                            <EditableFieldWithWarnings
                                onWarningClick={(e) => {
                                    e.stopPropagation();
                                    setDisplayedDocument({
                                        'key': documentKey,
                                        'page': chronologyItem.source?.page || 1
                                    });
                                }}
                                caseInfo={caseInfo}
                                setCaseInfo={setCaseInfo}
                                {...buildFieldPaths(documentKey, 'medical_chronology', itemIndex, 'patient_name')}
                                formatValue={getFormatFunction('patient')}
                                setSnackBarMessage={setSnackBarMessage}
                                style={{ fontWeight: 500 }}
                            />
                        </TableCell>
                        <TableCell sx={{
                            borderBottom: '1px solid var(--border-color)',
                        }}>
                            {renderChronologyTreatmentDates(chronologyItem, documentKey, itemIndex)}
                        </TableCell>
                        <TableCell sx={{
                            color: 'var(--text-primary)',
                            borderBottom: '1px solid var(--border-color)',
                            maxWidth: '200px'
                        }}>
                            {renderProviders(chronologyItem, documentKey, itemIndex, false)}
                        </TableCell>
                        <TableCell sx={{
                            color: 'var(--text-primary)',
                            borderBottom: '1px solid var(--border-color)',
                            maxWidth: '300px'
                        }}>
                            <EditableFieldWithWarnings
                                onWarningClick={(e) => {
                                    e.stopPropagation();
                                    setDisplayedDocument({
                                        'key': documentKey,
                                        'page': chronologyItem.source?.page || 1
                                    });
                                }}
                                caseInfo={caseInfo}
                                setCaseInfo={setCaseInfo}
                                {...buildFieldPaths(documentKey, 'medical_chronology', itemIndex, 'summary')}
                                formatValue={getFormatFunction('summary')}
                                setSnackBarMessage={setSnackBarMessage}
                            />
                        </TableCell>
                        <TableCell sx={{
                            color: 'var(--text-primary)',
                            borderBottom: '1px solid var(--border-color)',
                            maxWidth: '300px'
                        }}>
                            {formatEventDetails(chronologyItem, documentKey, itemIndex)}
                        </TableCell>
                        <TableCell sx={{
                            borderBottom: '1px solid var(--border-color)',
                        }}>
                            <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                                <Typography
                                    component="span"
                                    title="Click to view source document"
                                    sx={{
                                        cursor: 'pointer',
                                        textDecoration: 'underline',
                                        fontSize: '0.9rem',
                                    }}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        setDisplayedDocument({
                                            'key': chronologyItem['source_filekey'],
                                            'page': chronologyItem.source?.page || 1
                                        });
                                    }}
                                >
                                    {filename} {chronologyItem.source?.page && `(page ${chronologyItem.source?.page})`}
                                </Typography>
                                <IconButton
                                    className="source-view-icon"
                                    size="small"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        setDisplayedDocument({
                                            'key': chronologyItem['source_filekey'],
                                            'page': chronologyItem.source?.page || 1
                                        });
                                    }}
                                    sx={{
                                        padding: '2px',
                                        visibility: 'hidden',
                                        opacity: 0,
                                        transition: 'opacity 0.2s, visibility 0.2s',
                                        '&:hover': {
                                            opacity: 1,
                                            backgroundColor: 'rgba(0, 0, 0, 0.04)'
                                        }
                                    }}
                                    title="Click to view source document"
                                >
                                    <VisibilityIcon />
                                </IconButton>
                            </Box>
                        </TableCell>
                    </TableRow>
                );
            });

        return (
            <Table
                ref={tableRef}
                sx={{
                    minWidth: 650,
                    backgroundColor: 'var(--background-color)',
                    borderRadius: '8px',
                    overflow: 'hidden',
                    border: '1px solid var(--border-color)',
                    boxShadow: 'var(--shadow-light)'
                }}
                aria-label="medical chronology table"
            >
                <TableHead>
                    <TableRow sx={{ backgroundColor: 'var(--surface-color)' }}>
                        <TableCell sx={{
                            fontWeight: 600,
                            color: 'var(--text-primary)',
                            borderBottom: '2px solid var(--border-color)',
                            padding: '16px 12px',
                            minWidth: "100px"
                        }}>
                            Patient
                        </TableCell>
                        <TableCell sx={{
                            fontWeight: 600,
                            color: 'var(--text-primary)',
                            borderBottom: '2px solid var(--border-color)',
                            padding: '16px 12px',
                            minWidth: "120px"
                        }}>
                            Treatment Dates
                        </TableCell>
                        <TableCell sx={{
                            fontWeight: 600,
                            color: 'var(--text-primary)',
                            borderBottom: '2px solid var(--border-color)',
                            padding: '16px 12px',
                            minWidth: "200px"
                        }}>
                            Facility/Provider
                        </TableCell>
                        <TableCell sx={{
                            fontWeight: 600,
                            color: 'var(--text-primary)',
                            borderBottom: '2px solid var(--border-color)',
                            padding: '16px 12px',
                            minWidth: "300px"
                        }}>
                            Summary
                        </TableCell>
                        <TableCell sx={{
                            fontWeight: 600,
                            color: 'var(--text-primary)',
                            borderBottom: '2px solid var(--border-color)',
                            padding: '16px 12px',
                            minWidth: "600px"
                        }}>
                            Event Details
                        </TableCell>
                        <TableCell sx={{
                            fontWeight: 600,
                            color: 'var(--text-primary)',
                            borderBottom: '2px solid var(--border-color)',
                            padding: '16px 12px'
                        }}>
                            Source Document
                        </TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {tableBodyRows}
                </TableBody>
            </Table>
        );
    }

    function renderChronologyTreatmentDates(chronologyItem, documentKey, itemIndex) {
        const baseFieldPath = `$.documents['${documentKey}'].analysis.medical_chronology[${itemIndex}]`;
        return renderTreatmentDates(caseInfo, setCaseInfo, baseFieldPath, setSnackBarMessage, setDisplayedDocument);
    }

    function renderConsolidatedTreatmentDates(chronologyItem, reportIndex, itemIndex) {
        const baseFieldPath = `$.analysis.medical_reports[${reportIndex}].medical_chronology[${itemIndex}]`;
        return renderTreatmentDates(caseInfo, setCaseInfo, baseFieldPath, setSnackBarMessage, setDisplayedDocument);
    }

    function formatEventDetails(chronologyItem, documentKey, itemIndex) {
        const details = [];

        // Helper function to render individual event detail fields with review indicators
        const renderEventDetailField = (narrativeItem, eventIndex) => {
            if (!narrativeItem.value) return null;

            // Map item_type to display labels
            const getLabel = (itemType) => {
                const labelMap = {
                    'subjective_complaints': 'Complaints',
                    'objective_findings': 'Clinical Findings',
                    'assessment_diagnosis': 'Diagnosis',
                    'plan_treatment_rendered': 'Treatment',
                    'future_recommendations': 'Recommendations',
                    'causation_statement': 'Causation Statement',
                    'pre_existing_conditions_mentioned': 'Pre-existing Conditions',
                    'work_status_disability': 'Work Status'
                };
                return labelMap[itemType] || itemType;
            };

            const label = getLabel(narrativeItem.item_type);

            return (
                <Box key={`${narrativeItem.item_type}-${eventIndex}`} sx={{ mb: 1 }}>
                    <Box>
                        <strong>{label}:</strong>
                        <Box component="span" onClick={(e) => e.stopPropagation()}>
                            <EditableFieldWithWarnings
                                caseInfo={caseInfo}
                                setCaseInfo={setCaseInfo}
                                {...buildEventDetailPaths(documentKey, itemIndex, eventIndex, 'value')}
                                isEventDetail={true}
                                setSnackBarMessage={setSnackBarMessage}
                                onWarningClick={(e) => {
                                    e.stopPropagation();
                                    setDisplayedDocument({
                                        'key': documentKey,
                                        'page': chronologyItem.source?.page || 1
                                    });
                                }}
                            />
                        </Box>
                    </Box>
                </Box>
            );
        };

        // Process the detailed_narrative array
        if (chronologyItem.detailed_narrative && Array.isArray(chronologyItem.detailed_narrative)) {
            chronologyItem.detailed_narrative.forEach((narrativeItem, eventIndex) => {
                details.push(renderEventDetailField(narrativeItem, eventIndex));
            });
        }

        return details.length > 0 ? (
            <span>
                {details.map((detail, index) => (
                    <span key={index}>
                        {detail}
                        {index < details.length - 1 && <br />}
                    </span>
                ))}
            </span>
        ) : '';
    }

    function isValidMedicalChronologyItem(chronologyItem) {
        return chronologyItem && (chronologyItem['patient_name'] || chronologyItem['providers']?.length > 0 || chronologyItem['treatment_start_date']);
    }

    /**
     * Builds JSONPath expressions for consolidated medical report fields (single item, not array)
     * @param {number} reportIndex - Index of the medical report in the consolidated array
     * @param {string} dataType - Type of data (e.g., 'incident_summary')
     * @param {string} fieldName - Name of the field
     * @returns {Object} Object containing textPath, needsReviewPath, and caveatsPath
     */
    function buildConsolidatedSingleFieldPaths(reportIndex, dataType, fieldName) {
        return {
            textPath: `$.analysis.medical_reports[${reportIndex}].${dataType}.${fieldName}`,
            needsReviewPath: `$.analysis.medical_reports[${reportIndex}].${dataType}.needs_review.${fieldName}`,
            caveatsPath: `$.analysis.medical_reports[${reportIndex}].${dataType}.caveats.${fieldName}`
        }
    }

    /**
     * Builds JSONPath expressions for consolidated medical report fields
     * @param {number} reportIndex - Index of the medical report in the consolidated array
     * @param {string} dataType - Type of data (e.g., 'medical_chronology', 'past_medical_history')
     * @param {number} itemIndex - Index of the item in the array
     * @param {string} fieldName - Name of the field
     * @returns {Object} Object containing textPath, needsReviewPath, and caveatsPath
     */
    function buildConsolidatedFieldPaths(reportIndex, dataType, itemIndex, fieldName) {
        return {
            textPath: `$.analysis.medical_reports[${reportIndex}].${dataType}[${itemIndex}].${fieldName}`,
            needsReviewPath: `$.analysis.medical_reports[${reportIndex}].${dataType}[${itemIndex}].needs_review.${fieldName}`,
            caveatsPath: `$.analysis.medical_reports[${reportIndex}].${dataType}[${itemIndex}].caveats.${fieldName}`
        }
    }

    /**
     * Builds JSONPath expressions for provider fields in providers array (new schema)
     * @param {string} documentKey - Key of the document
     * @param {number} itemIndex - Index of the chronology item
     * @param {number} providerIndex - Index of the provider in the providers array
     * @param {string} fieldName - Name of the field ('provider_name' or 'provider_type')
     * @returns {Object} Object containing textPath, needsReviewPath, and caveatsPath
     */
    function buildProviderPaths(documentKey, itemIndex, providerIndex, fieldName) {
        return {
            textPath: `$.documents['${documentKey}'].analysis.medical_chronology[${itemIndex}].providers[${providerIndex}].${fieldName}`,
            needsReviewPath: `$.documents['${documentKey}'].analysis.medical_chronology[${itemIndex}].needs_review.providers`,
            caveatsPath: `$.documents['${documentKey}'].analysis.medical_chronology[${itemIndex}].caveats.providers`
        }
    }

    /**
     * Builds JSONPath expressions for provider fields in providers array (consolidated schema)
     * @param {number} reportIndex - Index of the medical report in the consolidated array
     * @param {number} itemIndex - Index of the chronology item
     * @param {number} providerIndex - Index of the provider in the providers array
     * @param {string} fieldName - Name of the field ('provider_name' or 'provider_type')
     * @returns {Object} Object containing textPath, needsReviewPath, and caveatsPath
     */
    function buildConsolidatedProviderPaths(reportIndex, itemIndex, providerIndex, fieldName) {
        return {
            textPath: `$.analysis.medical_reports[${reportIndex}].medical_chronology[${itemIndex}].providers[${providerIndex}].${fieldName}`,
            needsReviewPath: `$.analysis.medical_reports[${reportIndex}].medical_chronology[${itemIndex}].needs_review.providers`,
            caveatsPath: `$.analysis.medical_reports[${reportIndex}].medical_chronology[${itemIndex}].caveats.providers`
        }
    }

    /**
     * Renders provider information for a chronology item (handles both old and new schemas)
     * @param {Object} chronologyItem - The chronology item
     * @param {string} documentKey - Key of the document (for non-consolidated view)
     * @param {number} itemIndex - Index of the chronology item
     * @param {boolean} isConsolidated - Whether this is the consolidated view
     * @param {number} reportIndex - Index of the medical report (for consolidated view)
     * @returns {JSX.Element} The rendered provider information
     */
    function renderProviders(chronologyItem, documentKey, itemIndex, isConsolidated = false, reportIndex = 0) {
        const hasProvidersArray = Array.isArray(chronologyItem.providers) && chronologyItem.providers.length > 0;
        const hasOldSchema = chronologyItem.provider_name || chronologyItem.provider_type;
        const handleWarningClick = () => {
            if (isConsolidated) {
                const sourcesArray = chronologyItem.sources || [];
                if (sourcesArray.length > 0) {
                    setDisplayedDocument({
                        'key': getJSONPathCompatibleKey(sourcesArray[0]?.filename || ''),
                        'page': sourcesArray[0]?.page || 1
                    });
                }
            } else {
                setDisplayedDocument({
                    'key': documentKey,
                    'page': chronologyItem.source?.page || 1
                });
            }
        };

        return (
            <Box>
                {hasProvidersArray ? (
                    <>
                        {/* First provider */}
                        <strong>Provider:</strong>
                        <Box sx={{ mb: 1 }}>
                            <EditableFieldWithWarnings
                                caseInfo={caseInfo}
                                setCaseInfo={setCaseInfo}
                                {...(isConsolidated
                                    ? buildConsolidatedProviderPaths(reportIndex, itemIndex, 0, 'provider_name')
                                    : buildProviderPaths(documentKey, itemIndex, 0, 'provider_name'))}
                                setSnackBarMessage={setSnackBarMessage}
                                onWarningClick={handleWarningClick}
                            />
                            <EditableFieldWithWarnings
                                caseInfo={caseInfo}
                                setCaseInfo={setCaseInfo}
                                textPath={isConsolidated ?
                                    `$.analysis.medical_reports[${reportIndex}].medical_chronology[${itemIndex}].providers[0].provider_type` :
                                    `$.documents['${documentKey}'].analysis.medical_chronology[${itemIndex}].providers[0].provider_type`}
                                formatValue={getFormatFunction('provider_type')}
                                setSnackBarMessage={setSnackBarMessage}
                                onWarningClick={handleWarningClick}
                            />
                        </Box>
                        {/* Facility information */}
                        {(chronologyItem.facility_name || chronologyItem.facility_type) && (
                            <Box sx={{ mb: 1, ml: 0 }}>
                                <strong>Facility:</strong>
                                <EditableFieldWithWarnings
                                    caseInfo={caseInfo}
                                    setCaseInfo={setCaseInfo}
                                    {...(isConsolidated
                                        ? buildConsolidatedFieldPaths(reportIndex, 'medical_chronology', itemIndex, 'facility_name')
                                        : buildFieldPaths(documentKey, 'medical_chronology', itemIndex, 'facility_name'))}
                                    setSnackBarMessage={setSnackBarMessage}
                                    onWarningClick={handleWarningClick}
                                />
                                <EditableFieldWithWarnings
                                    caseInfo={caseInfo}
                                    setCaseInfo={setCaseInfo}
                                    textPath={isConsolidated ?
                                        `$.analysis.medical_reports[${reportIndex}].medical_chronology[${itemIndex}].facility_type` :
                                        `$.documents['${documentKey}'].analysis.medical_chronology[${itemIndex}].facility_type`}
                                    formatValue={getFormatFunction('facility_type')}
                                    setSnackBarMessage={setSnackBarMessage}
                                    onWarningClick={handleWarningClick}
                                />
                            </Box>
                        )}
                        {/* Remaining providers */}
                        {chronologyItem.providers.length > 1 && chronologyItem.providers.slice(1).map((provider, providerIndex) => {

                            const providerNameTextPath = isConsolidated ?
                                `$.analysis.medical_reports[${reportIndex}].medical_chronology[${itemIndex}].providers[${providerIndex + 1}].provider_name` :
                                `$.documents['${documentKey}'].analysis.medical_chronology[${itemIndex}].providers[${providerIndex + 1}].provider_name`;
                            const providerTypeTextPath = isConsolidated ?
                                `$.analysis.medical_reports[${reportIndex}].medical_chronology[${itemIndex}].providers[${providerIndex + 1}].provider_type` :
                                `$.documents['${documentKey}'].analysis.medical_chronology[${itemIndex}].providers[${providerIndex + 1}].provider_type`;
                            return (
                                < Box key={providerIndex + 1
                                } sx={{ mb: 1, mt: 1 }}>
                                    {providerIndex === 0 && <strong>Other Providers:</strong>}
                                    <EditableFieldWithWarnings
                                        caseInfo={caseInfo}
                                        setCaseInfo={setCaseInfo}
                                        textPath={providerNameTextPath}
                                        setSnackBarMessage={setSnackBarMessage}
                                        onWarningClick={handleWarningClick}
                                    />
                                    <EditableFieldWithWarnings
                                        caseInfo={caseInfo}
                                        setCaseInfo={setCaseInfo}
                                        textPath={providerTypeTextPath}
                                        formatValue={getFormatFunction('provider_type')}
                                        setSnackBarMessage={setSnackBarMessage}
                                        onWarningClick={handleWarningClick}
                                    />
                                </Box>
                            )
                        })}
                    </>
                ) : hasOldSchema ? (
                    <>
                        {/* Old schema fallback */}
                        <EditableFieldWithWarnings
                            caseInfo={caseInfo}
                            setCaseInfo={setCaseInfo}
                            {...(isConsolidated
                                ? buildConsolidatedFieldPaths(reportIndex, 'medical_chronology', itemIndex, 'provider_name')
                                : buildFieldPaths(documentKey, 'medical_chronology', itemIndex, 'provider_name'))}
                            setSnackBarMessage={setSnackBarMessage}
                            onWarningClick={handleWarningClick}
                        />
                        <EditableFieldWithWarnings
                            caseInfo={caseInfo}
                            setCaseInfo={setCaseInfo}
                            {...(isConsolidated
                                ? buildConsolidatedFieldPaths(reportIndex, 'medical_chronology', itemIndex, 'provider_type')
                                : buildFieldPaths(documentKey, 'medical_chronology', itemIndex, 'provider_type'))}
                            formatValue={getFormatFunction('provider_type')}
                            setSnackBarMessage={setSnackBarMessage}
                            onWarningClick={handleWarningClick}
                        />
                    </>
                ) : null
                }
            </Box >
        );
    }

}

// TODO: Move to helper
function assert(condition, message) {
    if (!condition) {
        throw new Error(`Invariant failed: ${message || "Condition not met."}`);
    }
}
